import React, { useCallback, useEffect, useState } from 'react';
import { useT, useLocale } from '../i18n/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import Callout from '../components/ui/Callout';
import StatCard from '../components/ui/StatCard';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import OtpModal from '../components/payouts/OtpModal';
import PayoutRequestModal from '../components/payouts/PayoutRequestModal';
import PayoutDestinationCard from '../components/payouts/PayoutDestinationCard';
import { PAYOUT_STATUS_TONE } from '../components/payouts/constants';
import { useApi } from '../api/client';
import { confirmPayout, getPayoutOverview, listPayouts, recheckPayout, requestPayoutOtp } from '../api/payouts';
import { formatCurrency, formatDate, formatNumber } from '../utils/format';

export default function PayoutsPage() {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const { hasPermission, hasRole } = useAuth();
  const canManage = hasRole('admin') && hasPermission('payouts:manage');

  const [overview, setOverview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const [requestOpen, setRequestOpen] = useState(false);
  const [pending, setPending] = useState(null); // { payload, challenge }
  const [result, setResult] = useState(null);
  const [rechecking, setRechecking] = useState(null);

  const load = useCallback(async () => {
    if (!useApi) return;
    setLoading(true);
    try {
      const [o, h] = await Promise.all([getPayoutOverview(), listPayouts()]);
      setOverview(o);
      setHistory(h);
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => { load(); }, [load]);

  if (!useApi) {
    return (
      <Card title={t('pages.payouts.title')} subtitle={t('pages.payouts.subtitle')}>
        <EmptyState icon="🔌" title={t('common.api_required')} />
      </Card>
    );
  }

  const newPayoutBlocker = !overview
    ? null
    : !canManage
      ? t('payouts_page.admin_only')
      : !overview.payoutMsisdn
        ? t('payouts_page.blocked_no_number')
        : !overview.gatewayConfigured
          ? t('payouts_page.gateway_missing')
          : !overview.mailConfigured
            ? t('payouts_page.mail_missing')
            : null;

  const handleRequest = async (payload) => {
    try {
      const challenge = await requestPayoutOtp(payload);
      setRequestOpen(false);
      setPending({ payload, challenge });
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    }
  };

  const handleResend = async () => {
    const challenge = await requestPayoutOtp(pending.payload);
    setPending((prev) => ({ ...prev, challenge }));
    toast.info(t('otp.resent'));
  };

  const handleConfirm = async (code) => {
    const payout = await confirmPayout({ challengeId: pending.challenge.challengeId, code });
    setPending(null);
    setResult(payout);
    setHistory((prev) => [payout, ...prev.filter((p) => p.id !== payout.id)]);
    // Balance moves as soon as the payout is recorded — refresh the cards.
    getPayoutOverview().then(setOverview).catch(() => {});
  };

  const handleRecheck = async (payout) => {
    setRechecking(payout.id);
    try {
      const fresh = await recheckPayout(payout.id);
      setHistory((prev) => prev.map((p) => (p.id === fresh.id ? fresh : p)));
      toast.success(t('payouts_page.toast_rechecked', { code: fresh.code, status: t(`payouts_page.statuses.${fresh.status}`) }));
      getPayoutOverview().then(setOverview).catch(() => {});
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setRechecking(null);
    }
  };

  const columns = [
    { key: 'code', header: t('common.reference'), cellClassName: 'mono', render: (r) => r.code },
    { key: 'date', header: t('common.date'), render: (r) => formatDate(r.createdAt, t) },
    { key: 'amount', header: t('common.amount'), render: (r) => <strong>{formatCurrency(r.amount, locale)}</strong> },
    {
      key: 'to',
      header: t('payouts_page.field_recipient'),
      render: (r) => (
        <div className="cell-stack">
          <span>{r.recipientName || '—'}</span>
          <span className="mono">{r.msisdn}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (r) => (
        <div className="cell-stack">
          <Badge tone={PAYOUT_STATUS_TONE[r.status]}>{t(`payouts_page.statuses.${r.status}`)}</Badge>
          {r.statusMessage && <span className="cell-sub">{r.statusMessage}</span>}
        </div>
      ),
    },
    { key: 'requestedBy', header: t('payouts_page.field_requested_by'), render: (r) => r.requestedBy || '—' },
    { key: 'note', header: t('payouts_page.field_note'), render: (r) => r.note || '—' },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (r) => (canManage && (r.status === 'pending' || r.status === 'completed') ? (
        <div className="action-row">
          <button type="button" className="action-btn" onClick={() => handleRecheck(r)} disabled={rechecking === r.id}>
            {rechecking === r.id ? t('common.loading') : t('payouts_page.recheck')}
          </button>
        </div>
      ) : '—'),
    },
  ];

  return (
    <div>
      <div className="stats-grid stats-grid--3">
        <StatCard
          label={t('payouts_page.collected')}
          value={formatNumber(overview?.collected || 0, locale)}
          change={t('payouts_page.collected_caption')}
          icon="💳"
          tone="sc4"
        />
        <StatCard
          label={t('payouts_page.paid_out')}
          value={formatNumber(overview?.paidOut || 0, locale)}
          change={t('payouts_page.paid_out_caption')}
          icon="📤"
          tone="sc1"
        />
        <StatCard
          label={t('payouts_page.available')}
          value={formatNumber(overview?.available || 0, locale)}
          change={t('payouts_page.available_caption')}
          changeTone="warn"
          icon="🏦"
          tone="sc2"
        />
      </div>

      {overview && !overview.gatewayConfigured && (
        <Callout tone="warning" title={t('payouts_page.gateway_missing')}>{t('common.server_secret_hint')}</Callout>
      )}
      {overview && !overview.mailConfigured && (
        <Callout tone="warning" title={t('payouts_page.mail_missing')}>{t('common.server_secret_hint')}</Callout>
      )}

      <div className="split-grid">
        <PayoutDestinationCard
          msisdn={overview?.payoutMsisdn}
          name={overview?.payoutName}
          providerId={overview?.payoutProviderId}
          updatedAt={overview?.payoutNumberUpdatedAt}
          canManage={canManage}
          mailConfigured={Boolean(overview?.mailConfigured)}
          onChanged={setOverview}
        />
        <Card title={t('payouts_page.send_title')} subtitle={t('payouts_page.send_subtitle')}>
          <p className="modal-body-text" style={{ marginBottom: 14 }}>{t('payouts_page.send_body')}</p>
          <Button variant="gold" onClick={() => setRequestOpen(true)} disabled={!overview || Boolean(newPayoutBlocker)}>
            + {t('payouts_page.new_payout')}
          </Button>
          {newPayoutBlocker && <div className="form-hint">{newPayoutBlocker}</div>}
        </Card>
      </div>

      <Card
        title={t('payouts_page.history_title')}
        subtitle={t('payouts_page.history_subtitle')}
        padded={false}
        actions={<Button size="sm" variant="ghost" onClick={load} loading={loading}>{t('common.refresh')}</Button>}
      >
        <DataTable
          columns={columns}
          rows={history}
          empty={<EmptyState icon="📤" title={loading ? t('common.loading') : t('payouts_page.history_empty')} />}
        />
      </Card>

      <PayoutRequestModal
        isOpen={requestOpen}
        onClose={() => setRequestOpen(false)}
        onSubmit={handleRequest}
        overview={overview}
      />

      <OtpModal
        isOpen={Boolean(pending)}
        onClose={() => setPending(null)}
        title={t('payouts_page.confirm_payout_title')}
        intro={pending ? (
          <div className="otp-summary">
            <div className="detail-item-label">{t('payouts_page.field_amount')}</div>
            <div className="otp-summary__value">
              {formatCurrency(pending.payload.amount, locale)} → {overview?.payoutMsisdn}
            </div>
            <div className="form-hint">{t('payouts_page.confirm_xentripay_otp')}</div>
          </div>
        ) : null}
        challenge={pending?.challenge}
        onConfirm={handleConfirm}
        onResend={handleResend}
        confirmLabel={t('payouts_page.send_payout')}
      />

      <Modal
        isOpen={Boolean(result)}
        onClose={() => setResult(null)}
        title={t('payouts_page.result_title')}
        size="sm"
        footer={<Button variant="primary" onClick={() => setResult(null)}>{t('common.close')}</Button>}
      >
        {result && (
          <>
            <div className="payout-result">
              <div className="mono">{result.code}</div>
              <div className="payout-result__amount">{formatCurrency(result.amount, locale)}</div>
              <Badge tone={PAYOUT_STATUS_TONE[result.status]}>{t(`payouts_page.statuses.${result.status}`)}</Badge>
            </div>
            {result.status === 'failed' || result.status === 'reversed' ? (
              <Callout tone="danger" title={t('payouts_page.result_failed')}>
                {result.statusMessage || t('payouts_page.result_failed_generic')}
              </Callout>
            ) : (
              <Callout tone="info" title={t('payouts_page.result_submitted')}>
                {t('payouts_page.result_approve_hint', { number: result.msisdn })}
                {result.statusMessage && <div className="form-hint">{result.statusMessage}</div>}
              </Callout>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
